import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { pscQueryKey } from '@/hooks/federal-contract/types'
import { fetchPscCodes } from '@/hooks/federal-contract/usePscCodes'
import { useCodeSearch } from '@/hooks/search/useCodeSearch'

import type { PaginatedResponse } from '@/types/Api'
import type { PSC } from '@/types/federal-contract/Api'

const fetchPSCSearch = async (
  query: string,
  accessToken: string,
): Promise<PaginatedResponse<PSC>> => {
  if (query.length < 1) {
    return { count: 0, has_more: false, data: [] }
  }
  const url = `${API_URL}/api/v1/psc/search?query=${query}&limit=50`
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: { status: res.status },
    })
  }
  return (await res.json()) as PaginatedResponse<PSC>
}

// Module-level stable references for useMemo deps in useCodeSearch
const pscSearchQueryKeyFn = (query: string) => pscQueryKey.search({ query })
const pscItemQueryKeyFn = (code: string) => pscQueryKey.code({ code })
const pscItemIdFn = (item: PSC) => item.code
const pscFetchItem = (code: string, accessToken: string) =>
  fetchPscCodes([code], accessToken).then((r) => r.data[0])

export const usePSCCodeSearch = ({
  initialCodes,
}: {
  initialCodes: string[]
}) =>
  useCodeSearch<PSC>({
    initialCodes,
    searchQueryKeyFn: pscSearchQueryKeyFn,
    itemQueryKeyFn: pscItemQueryKeyFn,
    fetchSearch: fetchPSCSearch,
    fetchItem: pscFetchItem,
    itemIdFn: pscItemIdFn,
  })
